// Utilitários do editor de design livre (canvas) das propostas.
// Página A4 a 96dpi — mesmas dimensões usadas no PDF (puppeteer) e no viewer público.
export const PAGE_W = 794;
export const PAGE_H = 1123;

export function uid() {
  return `el_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

export function novoGrupoId() {
  return `gp_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

export function formatCurrency(value) {
  if (value === undefined || value === null || Number.isNaN(value)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

export function formatDataExtenso(date) {
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }).format(date ? new Date(date) : new Date());
}

export function calcularTotais(itens = [], descontoGlobalPercent = 0) {
  const subtotal = (itens || []).reduce((acc, item) => {
    const bruto = (Number(item.quantidade) || 0) * (Number(item.preco_unitario) || 0);
    return acc + (bruto - (bruto * (Number(item.desconto_percent) || 0) / 100));
  }, 0);
  const total = subtotal - (subtotal * (Number(descontoGlobalPercent) || 0) / 100);
  return { subtotal, total };
}

// Catálogo de variáveis dinâmicas disponíveis no canvas (elemento 'variavel' e
// placeholders {{...}} dentro de textos).
export const CANVAS_VARIAVEIS = [
  { chave: 'contato.nome', label: 'Nome do cliente' },
  { chave: 'contato.email', label: 'E-mail do cliente' },
  { chave: 'contato.telefone', label: 'Telefone do cliente' },
  { chave: 'proposta.numero', label: 'Número da proposta' },
  { chave: 'proposta.titulo', label: 'Título da proposta' },
  { chave: 'proposta.data', label: 'Data da proposta' },
  { chave: 'proposta.validade', label: 'Data de validade' },
  { chave: 'proposta.valor_total', label: 'Valor total' },
  { chave: 'empresa.nome', label: 'Nome da empresa' },
  { chave: 'empresa.cnpj', label: 'CNPJ da empresa' },
  { chave: 'empresa.telefone', label: 'Telefone da empresa' },
  { chave: 'empresa.email', label: 'E-mail da empresa' },
  { chave: 'empresa.endereco', label: 'Endereço da empresa' },
];

function addDias(date, dias) {
  const d = date ? new Date(date) : new Date();
  d.setDate(d.getDate() + (Number(dias) || 0));
  return d;
}

export function resolverExpiracao(proposta) {
  return proposta?.expira_em ? new Date(proposta.expira_em) : addDias(proposta?.created_at, proposta?.validade_dias || 15);
}

export function resolverVariavel(chave, proposta) {
  const contato = proposta?.contato || {};
  const empresa = proposta?.layout?.empresa || {};
  const { total } = calcularTotais(proposta?.itens, proposta?.desconto_global_percent);
  const expira = resolverExpiracao(proposta);
  const mapa = {
    'contato.nome': contato.nome,
    'contato.email': contato.email,
    'contato.telefone': contato.telefone,
    'proposta.numero': proposta?.numero != null ? `#${proposta.numero}` : null,
    'proposta.titulo': proposta?.titulo,
    'proposta.data': formatDataExtenso(proposta?.created_at),
    'proposta.validade': formatDataExtenso(expira),
    'proposta.valor_total': formatCurrency(total),
    'empresa.nome': empresa.nome,
    'empresa.cnpj': empresa.cnpj,
    'empresa.telefone': empresa.telefone,
    'empresa.email': empresa.email,
    'empresa.endereco': empresa.endereco,
  };
  return mapa[chave];
}

export function substituirVariaveisHtml(html, proposta) {
  if (!html) return '';
  return html.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (match, chave) => {
    const valor = resolverVariavel(chave, proposta);
    return valor !== undefined && valor !== null && valor !== '' ? String(valor) : match;
  });
}

const DEFAULTS_POR_TIPO = {
  texto: {
    largura: 400, altura: 60,
    estilo: { fontSize: 14, color: '#374151', textAlign: 'left', fontWeight: 'normal', lineHeight: 1.6 },
    conteudo: { html: '<p>Digite o texto...</p>' },
  },
  imagem: {
    largura: 220, altura: 140,
    estilo: { borderRadius: 0, opacity: 1, objectFit: 'contain' },
    conteudo: { url: '' },
  },
  retangulo: {
    largura: 220, altura: 130,
    estilo: { background: '#F3E8FF', borderColor: '#7C3AED', borderWidth: 0, borderRadius: 8, opacity: 1 },
    conteudo: {},
  },
  linha: {
    largura: 300, altura: 3,
    estilo: { background: '#7C3AED', borderRadius: 2, opacity: 1 },
    conteudo: {},
  },
  itens: {
    largura: 700, altura: 240,
    estilo: {},
    conteudo: {},
  },
  variavel: {
    largura: 240, altura: 30,
    estilo: { fontSize: 14, color: '#111827', textAlign: 'left', fontWeight: 'normal' },
    conteudo: { chave: 'contato.nome' },
  },
  // Blocos dinâmicos pré-configurados (espelham blockTypes.js do editor de blocos):
  // conteúdo sempre calculado a partir da proposta, sem edição de texto livre.
  data: {
    largura: 300, altura: 26,
    estilo: { fontSize: 13, color: '#6b7280', textAlign: 'left', fontWeight: 'normal' },
    conteudo: { cidade: '' },
  },
  validade: {
    largura: 380, altura: 26,
    estilo: { fontSize: 13, color: '#374151', textAlign: 'left', fontWeight: 'normal' },
    conteudo: {},
  },
  dados_cliente: {
    largura: 320, altura: 68,
    estilo: { fontSize: 13, color: '#374151', textAlign: 'left', fontWeight: 'normal' },
    conteudo: {},
  },
};

export function novoElemento(tipo, overrides = {}) {
  const base = DEFAULTS_POR_TIPO[tipo] || DEFAULTS_POR_TIPO.texto;
  return {
    el_id: uid(),
    tipo,
    x: Math.round((PAGE_W - base.largura) / 2),
    y: 80,
    largura: base.largura,
    altura: base.altura,
    rotacao: 0,
    z: 0,
    estilo: { ...base.estilo },
    conteudo: { ...base.conteudo },
    ...overrides,
  };
}

// "Forma de pagamento" não é um tipo de elemento dedicado — é um texto livre com
// título pré-preenchido, igual ao bloco legado (blockTypes.js → novoBloco).
export function novoElementoFormaPagamento(overrides = {}) {
  return novoElemento('texto', {
    largura: 460,
    altura: 90,
    estilo: { fontSize: 13, color: '#374151', textAlign: 'left', fontWeight: 'normal', lineHeight: 1.6 },
    conteudo: { html: '<p><strong>Forma de pagamento</strong></p><p>Descreva aqui as condições de pagamento...</p>' },
    ...overrides,
  });
}

// Insere um bloco de texto no final da última página do canvas (nova página se não
// couber). Usado pela geração de proposta por IA para inserir observações/forma de
// pagamento quando o documento está em modo canvas (que não tem blocos "de texto"
// com papel fixo — só posições livres).
export function adicionarTextoAoCanvas(canvas, { titulo, html }) {
  const normalizado = normalizarCanvas(canvas);
  const paginas = normalizado.paginas.map(p => ({ ...p, elementos: [...p.elementos] }));
  let pagina = paginas[paginas.length - 1];

  const MARGEM_X = 48;
  const largura = PAGE_W - MARGEM_X * 2;
  const altura = 110;
  const maxY = pagina.elementos.reduce((acc, el) => Math.max(acc, (el.y || 0) + (el.altura || 0)), 40);
  let y = maxY + 16;

  if (y + altura > PAGE_H - 40) {
    pagina = novaPagina();
    paginas.push(pagina);
    y = 48;
  }

  const z = Math.max(0, ...pagina.elementos.map(el => el.z || 0)) + 1;
  const conteudoHtml = `${titulo ? `<p style="font-size:16px"><strong>${titulo}</strong></p>` : ''}${html}`;
  pagina.elementos = [...pagina.elementos, novoElemento('texto', {
    x: MARGEM_X, y, largura, altura, z,
    estilo: { fontSize: 13, color: '#374151', textAlign: 'left', fontWeight: 'normal', lineHeight: 1.6 },
    conteudo: { html: conteudoHtml },
  })];

  return { paginas };
}

export function novaPagina() {
  return { fundo_cor: '#ffffff', elementos: [] };
}

// Garante o shape mínimo { paginas: [ao menos 1] } para o editor.
export function normalizarCanvas(canvas) {
  if (canvas?.paginas?.length) return canvas;
  return { paginas: [novaPagina()] };
}

// Converte a cor de fundo das páginas numa CAMADA de retângulo full-bleed em vez de
// pintar a folha (fundo_cor). Assim o fundo aparece no painel de camadas e pode ser
// recolorido/removido como qualquer outro elemento. Nasce bloqueado para não capturar
// os cliques dos demais elementos — basta destravar no painel de camadas para editar.
export function fundoComoRetangulo(canvas, cor, { bloqueado = true } = {}) {
  const normalizado = normalizarCanvas(canvas);
  const hex = String(cor || '').trim().toLowerCase();
  if (!hex || hex === '#fff' || hex === '#ffffff') return normalizado;
  return {
    ...normalizado,
    paginas: normalizado.paginas.map(pagina => {
      // O fundo assume o z 0 e todos os demais sobem um degrau (z negativo ficaria
      // atrás da própria folha no editor/PDF, que usam z-index cru).
      const offset = 1 - Math.min(0, ...pagina.elementos.map(el => el.z || 0));
      return {
        ...pagina,
        fundo_cor: '#ffffff',
        elementos: [
          novoElemento('retangulo', {
            x: 0, y: 0, largura: PAGE_W, altura: PAGE_H, z: 0,
            nome: 'Fundo da página',
            bloqueado,
            estilo: { background: hex, borderColor: hex, borderWidth: 0, borderRadius: 0, opacity: 1 },
          }),
          ...pagina.elementos.map(el => ({ ...el, z: (el.z || 0) + offset })),
        ],
      };
    }),
  };
}

// ── Grupos de camadas ──
// Grupo é uma marcação PLANA: os elementos carregam `grupo_id` e a página guarda o
// nome em `grupos`. Nada muda para quem só renderiza (preview, PDF, link público),
// que continuam vendo uma lista simples de elementos ordenada por z.

// Monta a árvore de camadas na ordem FRENTE → FUNDO (a mesma do painel): cada grupo
// aparece na posição do seu membro mais à frente, com os demais aninhados dentro.
export function montarArvoreCamadas(elementos = [], grupos = []) {
  const ordenados = [...elementos].sort((a, b) => (b.z || 0) - (a.z || 0));
  const nos = [];
  const porGrupo = new Map();
  ordenados.forEach(el => {
    if (!el.grupo_id) {
      nos.push({ tipo: 'elemento', el });
      return;
    }
    let no = porGrupo.get(el.grupo_id);
    if (!no) {
      no = {
        tipo: 'grupo',
        grupo_id: el.grupo_id,
        nome: grupos.find(g => g.grupo_id === el.grupo_id)?.nome || 'Grupo',
        filhos: [],
      };
      porGrupo.set(el.grupo_id, no);
      nos.push(no);
    }
    no.filhos.push(el);
  });
  return nos;
}

// Achata a árvore de volta numa lista de el_id frente → fundo (entrada de
// reordenarCamadas) — mantém os membros de um grupo sempre contíguos na pilha.
export function achatarArvoreCamadas(nos = []) {
  return nos.flatMap(no => no.tipo === 'grupo' ? no.filhos.map(f => f.el_id) : [no.el.el_id]);
}

// O fundo de uma página pode estar em `fundo_cor` (folha pintada) ou numa CAMADA de
// retângulo full-bleed (modelos pré-prontos — ver fundoComoRetangulo). As duas funções
// abaixo leem/escrevem o fundo sem o chamador precisar saber qual das duas formas é.
function elementoFundoDaPagina(pagina) {
  return [...(pagina?.elementos || [])]
    .sort((a, b) => (a.z || 0) - (b.z || 0))
    .find(el => el.tipo === 'retangulo'
      && (el.x || 0) <= 0 && (el.y || 0) <= 0
      && (el.largura || 0) >= PAGE_W && (el.altura || 0) >= PAGE_H) || null;
}

export function corDeFundoCanvas(canvas) {
  const pagina = canvas?.paginas?.[0];
  if (!pagina) return '#ffffff';
  return elementoFundoDaPagina(pagina)?.estilo?.background || pagina.fundo_cor || '#ffffff';
}

export function aplicarCorDeFundo(canvas, cor) {
  const normalizado = normalizarCanvas(canvas);
  return {
    ...normalizado,
    paginas: normalizado.paginas.map(pagina => {
      const fundo = elementoFundoDaPagina(pagina);
      if (!fundo) return { ...pagina, fundo_cor: cor };
      return {
        ...pagina,
        elementos: pagina.elementos.map(el => el.el_id === fundo.el_id
          ? { ...el, estilo: { ...el.estilo, background: cor } }
          : el),
      };
    }),
  };
}

function esc(text) {
  return String(text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Converte o documento de blocos (fluxo vertical legado) em páginas de canvas.
// Cada bloco vira um elemento de texto/tabela empilhado; estoura para nova página
// quando passa da altura útil da folha.
export function blocosParaCanvas(proposta) {
  const MARGEM_X = 48;
  const LARGURA_UTIL = PAGE_W - MARGEM_X * 2;
  const LIMITE_Y = PAGE_H - 80;

  const paginas = [novaPagina()];
  let pagina = paginas[0];
  let y = 48;
  let z = 0;

  const pushElemento = (tipo, altura, overrides = {}) => {
    if (y + altura > LIMITE_Y) {
      pagina = novaPagina();
      paginas.push(pagina);
      y = 48;
    }
    pagina.elementos.push(novoElemento(tipo, {
      x: MARGEM_X,
      y,
      largura: LARGURA_UTIL,
      altura,
      z: z++,
      ...overrides,
    }));
    y += altura + 16;
  };

  const layout = proposta?.layout || {};
  const empresa = layout.empresa || {};

  // Cabeçalho: logo à esquerda, dados da empresa à direita
  if (layout.logo_url) {
    pagina.elementos.push(novoElemento('imagem', {
      x: MARGEM_X, y, largura: 160, altura: 56, z: z++,
      conteudo: { url: layout.logo_url },
    }));
  }
  const linhasEmpresa = [empresa.nome, empresa.cnpj, empresa.telefone].filter(Boolean);
  if (linhasEmpresa.length) {
    pagina.elementos.push(novoElemento('texto', {
      x: PAGE_W - MARGEM_X - 280, y, largura: 280, altura: 56, z: z++,
      estilo: { fontSize: 12, color: '#4b5563', textAlign: 'right', lineHeight: 1.5 },
      conteudo: { html: linhasEmpresa.map((l, i) => i === 0 ? `<p><strong>${esc(l)}</strong></p>` : `<p>${esc(l)}</p>`).join('') },
    }));
  }
  if (layout.logo_url || linhasEmpresa.length) y += 72;

  // Faixa decorativa na cor do tema
  pushElemento('linha', 3, {
    largura: LARGURA_UTIL,
    estilo: { background: layout.tema_cor || '#7C3AED', borderRadius: 2, opacity: 1 },
  });

  // Título
  pushElemento('texto', 44, {
    estilo: { fontSize: 24, color: '#111827', textAlign: 'left', fontWeight: 'bold' },
    conteudo: { html: `<p>${esc(proposta?.titulo || 'Proposta Comercial')}</p>` },
  });

  const blocos = [...(proposta?.blocos || [])].sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
  blocos.forEach(bloco => {
    const tipo = bloco.tipo || 'texto';
    if (tipo === 'itens' || tipo === 'tabela_precos') {
      if (bloco.titulo) {
        pushElemento('texto', 30, {
          estilo: { fontSize: 16, color: '#111827', fontWeight: 'bold', textAlign: 'left' },
          conteudo: { html: `<p>${esc(bloco.titulo)}</p>` },
        });
      }
      pushElemento('itens', 240);
      return;
    }
    if (tipo === 'data') {
      pushElemento('data', 26, { conteudo: { cidade: bloco.config?.cidade || '' } });
      return;
    }
    if (tipo === 'validade') {
      pushElemento('validade', 26);
      return;
    }
    if (tipo === 'dados_cliente') {
      pushElemento('dados_cliente', 68);
      return;
    }
    if (tipo === 'forma_pagamento' && !bloco.conteudo_html) {
      const preset = novoElementoFormaPagamento();
      pushElemento('texto', 90, { estilo: preset.estilo, conteudo: preset.conteudo });
      return;
    }
    // Blocos de texto (texto, forma_pagamento com conteúdo, observacoes e legados)
    const titulo = bloco.titulo ? `<p style="font-size:16px"><strong>${esc(bloco.titulo)}</strong></p>` : '';
    const corpo = bloco.conteudo_html || '';
    if (!titulo && !corpo) return;
    pushElemento('texto', 110, {
      estilo: { fontSize: 13, color: '#374151', textAlign: 'left', lineHeight: 1.6 },
      conteudo: { html: `${titulo}${corpo}` },
    });
  });

  // Rodapé
  if (layout.rodape_texto) {
    pushElemento('texto', 30, {
      estilo: { fontSize: 11, color: '#9ca3af', textAlign: 'left' },
      conteudo: { html: `<p>${esc(layout.rodape_texto)}</p>` },
    });
  }

  return { paginas };
}

// Estima a altura renderizada do HTML de uma seção (13px, lineHeight 1.7 ≈ 22px por
// linha). Generosa de propósito: o elemento de texto do canvas tem overflow-hidden,
// então subestimar corta conteúdo; sobrar um pouco de espaço é inofensivo.
function estimarAlturaHtmlSecao(html, largura) {
  const charsPorLinha = Math.max(30, Math.floor(largura / 6.8));
  const paragrafos = String(html || '')
    .split(/<\/(?:p|li)>/i)
    .map(p => p.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  const linhas = paragrafos.reduce((acc, p) => acc + Math.max(1, Math.ceil(p.length / charsPorLinha)), 0) || 1;
  return Math.max(40, linhas * 22 + 24);
}

// Monta o documento COMPLETO da proposta no canvas a partir das seções geradas pela
// IA (campo "documento" do /gerar-com-ia): cabeçalho hero (faixa escura com logo e
// título), linha de informações (cliente/data/validade), seções com chip numerado
// na cor do tema, destaques, tabela de itens, assinatura e rodapé em toda página.
// Substitui todo o conteúdo visual do canvas — usado quando o agente gera ou
// reestrutura a proposta inteira, não em ajustes pontuais.
export function documentoIaParaCanvas(documento, { titulo, fundoCor, temaCor, logoUrl, empresaNome, assinaturaNome, temContato, compacto, estilo } = {}) {
  const MARGEM_X = 56;
  const LARGURA_UTIL = PAGE_W - MARGEM_X * 2;
  const LIMITE_Y = PAGE_H - 96; // reserva o rodapé
  const ACENTO = temaCor || '#7C3AED';
  const HEADER_BG = '#0f172a';
  // Estilo visual do documento — cada um com cabeçalho, títulos de seção e caixa de
  // destaque próprios: 'hero' (faixa escura + chips numerados), 'minimal' (editorial,
  // régua grossa e seções em caixa alta), 'faixa' (filete lateral em todas as
  // páginas), 'blocado' (banda de cor no topo + títulos em tarja cheia), 'central'
  // (composição centrada) e 'geometrico' (círculos decorativos + chips vazados).
  const ESTILO = ['hero', 'minimal', 'faixa', 'blocado', 'central', 'geometrico'].includes(estilo) ? estilo : 'hero';
  // `compacto` aperta cabeçalho/espçamentos para o documento caber em UMA página
  // (usado nos modelos pré-prontos); o modo normal respira mais e pagina livremente.
  const DIM = compacto
    ? { headerComLogo: 176, headerSemLogo: 140, gapPosHeader: 16, gapSecao: 16, gapTitulo: 6, alturaItens: 200, gapDestaque: 12, gapValidade: 20, gapSeparador: 16 }
    : { headerComLogo: 212, headerSemLogo: 172, gapPosHeader: 26, gapSecao: 28, gapTitulo: 10, alturaItens: 240, gapDestaque: 18, gapValidade: 28, gapSeparador: 28 };

  const paginas = [{ fundo_cor: fundoCor || '#ffffff', elementos: [] }];
  let pagina = paginas[0];
  let y = 0;
  let z = 0;

  const addEl = (tipo, overrides) => {
    pagina.elementos.push(novoElemento(tipo, { z: z++, ...overrides }));
  };

  const quebraSePreciso = (altura) => {
    if (y + altura > LIMITE_Y) {
      pagina = { fundo_cor: fundoCor || '#ffffff', elementos: [] };
      paginas.push(pagina);
      y = 64;
    }
  };

  const pushElemento = (tipo, altura, overrides = {}, gap = 14) => {
    quebraSePreciso(altura);
    addEl(tipo, { x: MARGEM_X, y, largura: LARGURA_UTIL, altura, ...overrides });
    y += altura + gap;
  };

  // Caixa de destaque: um único elemento de texto com HTML/CSS inline, com visual
  // próprio por estilo. O mesmo HTML renderiza idêntico no editor, no PDF
  // (puppeteer) e no link público (DOMPurify permite div/span/style inline).
  const pushDestaque = (html) => {
    const alturaTexto = estimarAlturaHtmlSecao(html, LARGURA_UTIL - 40);
    const alturaCaixa = alturaTexto + 30;
    let wrap;
    if (ESTILO === 'blocado') {
      wrap = `<div style="background:${ACENTO};border-radius:8px;padding:13px 18px;color:#ffffff">${html}</div>`;
    } else if (ESTILO === 'minimal') {
      wrap = `<div style="border-top:2px solid #111827;border-bottom:2px solid #111827;padding:12px 4px">${html}</div>`;
    } else if (ESTILO === 'central') {
      wrap = `<div style="border-top:2px solid ${ACENTO};border-bottom:2px solid ${ACENTO};padding:12px 16px;text-align:center">${html}</div>`;
    } else if (ESTILO === 'geometrico') {
      wrap = `<div style="border:2px dashed ${ACENTO};border-radius:12px;padding:12px 16px">${html}</div>`;
    } else {
      wrap = `<div style="background:#f8fafc;border:1px solid #e2e8f0;border-left:4px solid ${ACENTO};border-radius:8px;padding:13px 18px">${html}</div>`;
    }
    pushElemento('texto', alturaCaixa, {
      estilo: { fontSize: 13, color: '#334155', textAlign: 'left', fontWeight: 'normal', lineHeight: 1.7 },
      conteudo: { html: wrap },
    }, DIM.gapDestaque);
  };

  // Título de seção, com visual próprio por estilo (HTML/CSS inline num único elemento).
  const pushTituloSecao = (tituloSecao) => {
    let inner;
    let alturaTitulo = 30;
    const m = /^\s*(\d+)[.)]\s*(.*)$/.exec(tituloSecao);
    if (ESTILO === 'minimal') {
      // Caixa alta com sublinhado grosso na cor do tema (só sob o texto)
      const textoTitulo = m && m[2] ? m[2] : tituloSecao;
      inner = `<div><p style="display:inline-block;font-size:13px;font-weight:800;letter-spacing:0.08em;text-transform:uppercase;color:#111827;padding-bottom:5px;border-bottom:3px solid ${ACENTO}">${esc(textoTitulo)}</p></div>`;
      alturaTitulo = 32;
    } else if (ESTILO === 'blocado') {
      // Tarja cheia na cor do tema com texto branco
      const textoTitulo = m && m[2] ? m[2] : tituloSecao;
      inner = `<div style="background:${ACENTO};color:#ffffff;font-weight:700;font-size:14px;padding:7px 14px;border-radius:6px;letter-spacing:0.02em">${esc(textoTitulo)}</div>`;
      alturaTitulo = 34;
    } else if (ESTILO === 'central') {
      // Centralizado com traço curto de acento abaixo
      const textoTitulo = m && m[2] ? m[2] : tituloSecao;
      inner = `<div style="text-align:center"><p style="font-size:16px;font-weight:700;color:#111827">${esc(textoTitulo)}</p><span style="display:inline-block;width:40px;height:3px;background:${ACENTO};border-radius:2px;margin-top:5px"></span></div>`;
      alturaTitulo = 42;
    } else if (ESTILO === 'geometrico') {
      // Chip numerado vazado (círculo com borda na cor do tema)
      inner = m && m[2]
        ? `<div style="display:flex;align-items:center;gap:12px"><span style="display:inline-flex;width:26px;height:26px;border-radius:50%;border:2px solid ${ACENTO};color:${ACENTO};font-weight:700;font-size:12px;align-items:center;justify-content:center;flex-shrink:0">${m[1]}</span><span style="font-size:16px;font-weight:700;color:#111827">${esc(m[2])}</span></div>`
        : `<p style="font-size:16px;font-weight:700;color:#111827">${esc(tituloSecao)}</p>`;
    } else if (ESTILO === 'faixa') {
      // Barra vertical de acento ao lado do título
      const textoTitulo = m && m[2] ? m[2] : tituloSecao;
      inner = `<div style="display:flex;align-items:center;gap:10px"><span style="display:inline-block;width:4px;height:18px;background:${ACENTO};border-radius:2px;flex-shrink:0"></span><span style="font-size:16px;font-weight:700;color:#111827">${esc(textoTitulo)}</span></div>`;
    } else {
      // hero: chip numerado na cor do tema ("1. Escopo" → [1] Escopo)
      inner = m && m[2]
        ? `<div style="display:flex;align-items:center;gap:12px"><span style="display:inline-flex;width:26px;height:26px;border-radius:8px;background:${ACENTO};color:#ffffff;font-weight:700;font-size:13px;align-items:center;justify-content:center;flex-shrink:0">${m[1]}</span><span style="font-size:16px;font-weight:700;color:#111827">${esc(m[2])}</span></div>`
        : `<p style="font-size:16px;font-weight:700;color:#111827">${esc(tituloSecao)}</p>`;
    }
    pushElemento('texto', alturaTitulo, {
      estilo: { fontSize: 16, color: '#111827', textAlign: 'left', fontWeight: 'bold', lineHeight: 1.6 },
      conteudo: { html: inner },
    }, DIM.gapTitulo);
  };

  // Linha de informações compartilhada: cliente à esquerda, data à direita, validade abaixo.
  const pushInfoRow = ({ comData = true } = {}) => {
    if (temContato) {
      addEl('dados_cliente', { x: MARGEM_X, y, largura: 340, altura: 58 });
    }
    if (comData) {
      addEl('texto', {
        x: PAGE_W - MARGEM_X - 240, y, largura: 240, altura: 16,
        estilo: { fontSize: 10, color: '#9ca3af', textAlign: 'right', fontWeight: 'bold' },
        conteudo: { html: '<p>DATA</p>' },
      });
      addEl('data', {
        x: PAGE_W - MARGEM_X - 240, y: y + 16, largura: 240, altura: 22,
        estilo: { fontSize: 13, color: '#374151', textAlign: 'right', fontWeight: 'normal' },
      });
    }
    y += (temContato ? 58 : (comData ? 42 : 0)) + 6;
    addEl('validade', {
      x: MARGEM_X, y, largura: LARGURA_UTIL, altura: 22,
      estilo: { fontSize: 12, color: '#6b7280', textAlign: 'left', fontWeight: 'normal' },
    });
    y += DIM.gapValidade;
  };

  if (ESTILO === 'blocado') {
    // ── Cabeçalho blocado: banda cheia na cor do tema com título branco ──
    const hBand = compacto ? (logoUrl ? 124 : 108) : (logoUrl ? 148 : 128);
    addEl('retangulo', {
      x: 0, y: 0, largura: PAGE_W, altura: hBand,
      estilo: { background: ACENTO, borderWidth: 0, borderRadius: 0, opacity: 1 },
    });
    if (logoUrl) {
      addEl('retangulo', {
        x: PAGE_W - MARGEM_X - 140, y: compacto ? 20 : 26, largura: 140, altura: 48,
        estilo: { background: '#ffffff', borderWidth: 0, borderRadius: 10, opacity: 1 },
      });
      addEl('imagem', {
        x: PAGE_W - MARGEM_X - 130, y: (compacto ? 20 : 26) + 8, largura: 120, altura: 32,
        estilo: { objectFit: 'contain', borderRadius: 0, opacity: 1 },
        conteudo: { url: logoUrl },
      });
    }
    const yTituloBand = compacto ? 26 : 34;
    addEl('texto', {
      x: MARGEM_X, y: yTituloBand, largura: LARGURA_UTIL - (logoUrl ? 160 : 0), altura: 40,
      estilo: { fontSize: 28, color: '#ffffff', textAlign: 'left', fontWeight: 'bold' },
      conteudo: { html: '<p>PROPOSTA COMERCIAL</p>' },
    });
    if (titulo) {
      addEl('texto', {
        x: MARGEM_X, y: yTituloBand + 44, largura: LARGURA_UTIL - (logoUrl ? 160 : 0), altura: 22,
        estilo: { fontSize: 13, color: '#f1f5f9', textAlign: 'left', fontWeight: 'normal' },
        conteudo: { html: `<p>${esc(titulo)}</p>` },
      });
    }
    y = hBand + DIM.gapPosHeader;
    pushInfoRow();
    pushElemento('linha', 1, {
      estilo: { background: '#e5e7eb', borderRadius: 0, opacity: 1 },
    }, DIM.gapSeparador);
  } else if (ESTILO === 'central') {
    // ── Cabeçalho central: composição toda centrada, elegante ──
    y = compacto ? 40 : 56;
    if (logoUrl) {
      addEl('imagem', {
        x: Math.round((PAGE_W - 120) / 2), y, largura: 120, altura: 40,
        estilo: { objectFit: 'contain', borderRadius: 0, opacity: 1 },
        conteudo: { url: logoUrl },
      });
      y += compacto ? 48 : 56;
    }
    addEl('texto', {
      x: MARGEM_X, y, largura: LARGURA_UTIL, altura: 40,
      estilo: { fontSize: 28, color: '#111827', textAlign: 'center', fontWeight: 'bold' },
      conteudo: { html: '<p>PROPOSTA COMERCIAL</p>' },
    });
    y += 44;
    if (titulo) {
      addEl('texto', {
        x: MARGEM_X, y, largura: LARGURA_UTIL, altura: 22,
        estilo: { fontSize: 13, color: '#6b7280', textAlign: 'center', fontWeight: 'normal' },
        conteudo: { html: `<p>${esc(titulo)}</p>` },
      });
      y += 26;
    }
    addEl('linha', {
      x: Math.round((PAGE_W - 56) / 2), y, largura: 56, altura: 3,
      estilo: { background: ACENTO, borderRadius: 2, opacity: 1 },
    });
    y += compacto ? 20 : 28;
    pushInfoRow();
    pushElemento('linha', 1, {
      estilo: { background: '#e5e7eb', borderRadius: 0, opacity: 1 },
    }, DIM.gapSeparador);
  } else if (ESTILO === 'geometrico') {
    // ── Cabeçalho geométrico: círculos decorativos no canto superior direito
    // (transbordam a página de propósito — todas as superfícies clipam com
    // overflow hidden) ──
    addEl('retangulo', {
      x: PAGE_W - 150, y: -70, largura: 220, altura: 220,
      estilo: { background: ACENTO, borderWidth: 0, borderRadius: 110, opacity: 1 },
    });
    addEl('retangulo', {
      x: PAGE_W - 235, y: 44, largura: 90, altura: 90,
      estilo: { background: ACENTO, borderWidth: 0, borderRadius: 45, opacity: 0.25 },
    });
    y = compacto ? 44 : 58;
    if (logoUrl) {
      addEl('imagem', {
        x: MARGEM_X, y: y - 10, largura: 120, altura: 40,
        estilo: { objectFit: 'contain', borderRadius: 0, opacity: 1 },
        conteudo: { url: logoUrl },
      });
      y += compacto ? 40 : 48;
    }
    addEl('texto', {
      x: MARGEM_X, y, largura: LARGURA_UTIL - 200, altura: 40,
      estilo: { fontSize: 28, color: '#111827', textAlign: 'left', fontWeight: 'bold' },
      conteudo: { html: '<p>PROPOSTA COMERCIAL</p>' },
    });
    y += 44;
    if (titulo) {
      addEl('texto', {
        x: MARGEM_X, y, largura: LARGURA_UTIL - 200, altura: 22,
        estilo: { fontSize: 13, color: '#6b7280', textAlign: 'left', fontWeight: 'normal' },
        conteudo: { html: `<p>${esc(titulo)}</p>` },
      });
      y += 28;
    }
    addEl('linha', {
      x: MARGEM_X, y, largura: 64, altura: 4,
      estilo: { background: ACENTO, borderRadius: 2, opacity: 1 },
    });
    y += compacto ? 18 : 26;
    pushInfoRow();
    pushElemento('linha', 1, {
      estilo: { background: '#e5e7eb', borderRadius: 0, opacity: 1 },
    }, DIM.gapSeparador);
  } else if (ESTILO === 'minimal') {
    // ── Cabeçalho minimal: barra fina de acento no topo, título grande com data na
    // mesma linha e régua grossa escura (referência editorial/Canva) ──
    addEl('retangulo', {
      x: 0, y: 0, largura: PAGE_W, altura: 8,
      estilo: { background: ACENTO, borderWidth: 0, borderRadius: 0, opacity: 1 },
    });
    y = compacto ? 32 : 42;
    if (logoUrl) {
      addEl('imagem', {
        x: PAGE_W - MARGEM_X - 120, y, largura: 120, altura: 40,
        estilo: { objectFit: 'contain', borderRadius: 0, opacity: 1 },
        conteudo: { url: logoUrl },
      });
      y += compacto ? 48 : 56;
    }
    addEl('texto', {
      x: MARGEM_X, y, largura: LARGURA_UTIL - 180, altura: 40,
      estilo: { fontSize: 28, color: '#111827', textAlign: 'left', fontWeight: 'bold' },
      conteudo: { html: '<p>PROPOSTA COMERCIAL</p>' },
    });
    addEl('data', {
      x: PAGE_W - MARGEM_X - 170, y: y + 14, largura: 170, altura: 20,
      estilo: { fontSize: 12, color: '#111827', textAlign: 'right', fontWeight: 'bold' },
    });
    y += 46;
    addEl('linha', {
      x: MARGEM_X, y, largura: LARGURA_UTIL, altura: 3,
      estilo: { background: '#111827', borderRadius: 0, opacity: 1 },
    });
    y += 12;
    if (titulo) {
      addEl('texto', {
        x: MARGEM_X, y, largura: LARGURA_UTIL, altura: 22,
        estilo: { fontSize: 13, color: '#6b7280', textAlign: 'left', fontWeight: 'normal' },
        conteudo: { html: `<p>${esc(titulo)}</p>` },
      });
      y += 26;
    }
    pushInfoRow({ comData: false });
    y += compacto ? 6 : 12;
  } else if (ESTILO === 'faixa') {
    // ── Cabeçalho faixa: fundo claro, título escuro com traço de acento; o filete
    // lateral na cor do tema entra em todas as páginas no passe final ──
    y = compacto ? 40 : 54;
    if (logoUrl) {
      addEl('imagem', {
        x: PAGE_W - MARGEM_X - 130, y: y - 4, largura: 130, altura: 44,
        estilo: { objectFit: 'contain', borderRadius: 0, opacity: 1 },
        conteudo: { url: logoUrl },
      });
    }
    addEl('texto', {
      x: MARGEM_X, y, largura: LARGURA_UTIL - 150, altura: 40,
      estilo: { fontSize: 28, color: '#111827', textAlign: 'left', fontWeight: 'bold' },
      conteudo: { html: '<p>PROPOSTA COMERCIAL</p>' },
    });
    y += 44;
    if (titulo) {
      addEl('texto', {
        x: MARGEM_X, y, largura: LARGURA_UTIL, altura: 22,
        estilo: { fontSize: 13, color: '#6b7280', textAlign: 'left', fontWeight: 'normal' },
        conteudo: { html: `<p>${esc(titulo)}</p>` },
      });
      y += 28;
    }
    addEl('linha', {
      x: MARGEM_X, y, largura: 64, altura: 4,
      estilo: { background: ACENTO, borderRadius: 2, opacity: 1 },
    });
    y += compacto ? 18 : 26;
    pushInfoRow();
    pushElemento('linha', 1, {
      estilo: { background: '#e5e7eb', borderRadius: 0, opacity: 1 },
    }, DIM.gapSeparador);
  } else {
    // ── Cabeçalho hero (faixa escura full-bleed) ──
    const headerH = logoUrl ? DIM.headerComLogo : DIM.headerSemLogo;
    addEl('retangulo', {
      x: 0, y: 0, largura: PAGE_W, altura: headerH,
      estilo: { background: HEADER_BG, borderWidth: 0, borderRadius: 0, opacity: 1 },
    });
    addEl('retangulo', {
      x: 0, y: headerH, largura: PAGE_W, altura: 4,
      estilo: { background: ACENTO, borderWidth: 0, borderRadius: 0, opacity: 1 },
    });
    let yTitulo = compacto ? 36 : 58;
    if (logoUrl) {
      // Chip branco atrás da logo — funciona com logos de qualquer cor sobre a faixa escura
      const yChip = compacto ? 22 : 34;
      const hChip = compacto ? 48 : 56;
      addEl('retangulo', {
        x: MARGEM_X, y: yChip, largura: 150, altura: hChip,
        estilo: { background: '#ffffff', borderWidth: 0, borderRadius: 10, opacity: 1 },
      });
      addEl('imagem', {
        x: MARGEM_X + 10, y: yChip + 8, largura: 130, altura: hChip - 16,
        estilo: { objectFit: 'contain', borderRadius: 0, opacity: 1 },
        conteudo: { url: logoUrl },
      });
      yTitulo = compacto ? 84 : 112;
    }
    addEl('texto', {
      x: MARGEM_X, y: yTitulo, largura: LARGURA_UTIL, altura: 42,
      estilo: { fontSize: 30, color: '#ffffff', textAlign: 'left', fontWeight: 'bold' },
      conteudo: { html: '<p>PROPOSTA COMERCIAL</p>' },
    });
    if (titulo) {
      addEl('texto', {
        x: MARGEM_X, y: yTitulo + 46, largura: LARGURA_UTIL, altura: 22,
        estilo: { fontSize: 13, color: '#94a3b8', textAlign: 'left', fontWeight: 'normal' },
        conteudo: { html: `<p>${esc(titulo)}</p>` },
      });
    }
    y = headerH + 4 + DIM.gapPosHeader;
    pushInfoRow();
    pushElemento('linha', 1, {
      estilo: { background: '#e5e7eb', borderRadius: 0, opacity: 1 },
    }, DIM.gapSeparador);
  }

  // ── Seções ──
  (documento?.secoes || []).forEach(secao => {
    if (secao.titulo) {
      // Quebra ANTES do título quando o corpo não couber junto — evita título órfão
      // no fim da página (tabela de itens e destaques são atômicos; texto corrido
      // pode ao menos começar na mesma página).
      const alturaCorpo = secao.tipo === 'itens'
        ? DIM.alturaItens
        : secao.tipo === 'destaque'
          ? estimarAlturaHtmlSecao(secao.html, LARGURA_UTIL - 44) + 24
          : secao.html ? Math.min(estimarAlturaHtmlSecao(secao.html, LARGURA_UTIL), 80) : 0;
      quebraSePreciso(26 + 12 + alturaCorpo);
      pushTituloSecao(secao.titulo);
    }
    if (secao.tipo === 'itens') {
      pushElemento('itens', DIM.alturaItens, {}, DIM.gapSecao);
      return;
    }
    if (!secao.html) return;
    if (secao.tipo === 'destaque') {
      pushDestaque(secao.html);
      return;
    }
    pushElemento('texto', estimarAlturaHtmlSecao(secao.html, LARGURA_UTIL), {
      estilo: { fontSize: 13, color: '#374151', textAlign: 'left', fontWeight: 'normal', lineHeight: 1.7 },
      conteudo: { html: secao.html },
    }, DIM.gapSecao);
  });

  // ── Assinatura ──
  if (assinaturaNome || empresaNome) {
    y += 18;
    quebraSePreciso(84);
    pushElemento('linha', 1, {
      largura: 220,
      estilo: { background: '#d1d5db', borderRadius: 0, opacity: 1 },
    }, 10);
    pushElemento('texto', 46, {
      estilo: { fontSize: 13, color: '#111827', textAlign: 'left', fontWeight: 'bold', lineHeight: 1.5 },
      conteudo: {
        html: `<p>${esc(assinaturaNome || empresaNome)}</p>${assinaturaNome && empresaNome ? `<p style="font-weight:normal">${esc(empresaNome)}</p>` : ''}`,
      },
    });
  }

  // ── Rodapé (e filete lateral do estilo 'faixa') em todas as páginas ──
  const totalPaginas = paginas.length;
  paginas.forEach((p, i) => {
    let zRodape = Math.max(0, ...p.elementos.map(el => el.z || 0)) + 1;
    if (ESTILO === 'faixa') {
      p.elementos.push(novoElemento('retangulo', {
        x: 0, y: 0, largura: 10, altura: PAGE_H, z: zRodape++,
        estilo: { background: ACENTO, borderWidth: 0, borderRadius: 0, opacity: 1 },
      }));
    }
    p.elementos.push(novoElemento('linha', {
      x: MARGEM_X, y: PAGE_H - 60, largura: LARGURA_UTIL, altura: 1, z: zRodape++,
      estilo: { background: '#e5e7eb', borderRadius: 0, opacity: 1 },
    }));
    if (empresaNome) {
      p.elementos.push(novoElemento('texto', {
        x: MARGEM_X, y: PAGE_H - 50, largura: 340, altura: 20, z: zRodape++,
        estilo: { fontSize: 11, color: '#9ca3af', textAlign: 'left', fontWeight: 'normal' },
        conteudo: { html: `<p>${esc(empresaNome)}</p>` },
      }));
    }
    p.elementos.push(novoElemento('texto', {
      x: PAGE_W - MARGEM_X - 200, y: PAGE_H - 50, largura: 200, altura: 20, z: zRodape++,
      estilo: { fontSize: 11, color: '#9ca3af', textAlign: 'right', fontWeight: 'normal' },
      conteudo: { html: `<p>Página ${i + 1} de ${totalPaginas}</p>` },
    }));
  });

  return { paginas };
}
